const FLIGHT_PROMPT = `You are extracting flight events from a travel confirmation for a touring artist's day sheet.

For EVERY flight, you MUST create exactly TWO separate events:

EVENT 1 - DEPARTURE:
- type: "flight_departure"
- title: "[Passenger First Name]'s flight to [Destination City]"
  Example: "Kevin's flight to Los Angeles"
- date: departure date YYYY-MM-DD
- timeStart: departure time in HH:MM (24hr, local departure timezone)
- timeEnd: null
- location: "[Departure Airport Name], [City]"
- notes: "[TIME] [TIMEZONE]: Depart [ORIGIN CODE]-[DEST CODE] [AIRLINE] [FLIGHT#] Seat: [SEAT] Confirmation: [CONF CODE] Flight time: [DURATION]"
- flightNumber: e.g. "UA2434"
- departureAirport: airport code e.g. "EWR"
- arrivalAirport: airport code e.g. "LAX"
- passengerInitials: initials e.g. "KJ"

EVENT 2 - ARRIVAL:
- type: "flight_arrival"
- title: "[Passenger First Name] arrives [Destination City]"
- date: arrival date YYYY-MM-DD
- timeStart: arrival time in HH:MM (24hr, local DESTINATION timezone)
- timeEnd: null
- location: "[Arrival Airport Name], [City]"
- notes: "[TIME] [TIMEZONE]: Arrive [DEST CODE] [AIRLINE] [FLIGHT#] Seat: [SEAT] Confirmation: [CONF CODE]"
- flightNumber, departureAirport, arrivalAirport, passengerInitials: same as departure

Also extract any hotels, ground transport, or other events.

TIMEZONE RULES:
- Always convert to the LOCAL time at each airport
- If confirmation shows times in one timezone, convert arrival to destination local time

NAME RULES:
- Use passenger's first name only in the title
- JONAS II/PAUL KEVIN = Kevin, JONAS/DANIELLE VIVIAN = Danielle
- passengerInitials: KJ for Kevin Jonas, DJ for Danielle Jonas, etc.`;

const SCHEDULE_PROMPT = `You are extracting events from a production schedule, press itinerary, or tour document for an artist's day sheet app.

Extract EVERY event, appointment, interview, appearance, arrival, departure, break, grooming session, etc. into individual events.

RULES:
- Each time slot = one event
- Use the EXACT times from the document in 24-hour HH:MM format
- If a time range is given (e.g. "11:00 AM - 2:00 PM"), set timeStart and timeEnd
- For single times (e.g. "6:45 AM"), set only timeStart, timeEnd = null
- Include ALL details in notes: interviewer names, concepts, run of show details, air dates, contact info
- For hotel info, use type "hotel"
- For transport/transfers, use type "transport"
- For grooming/styling, use type "event"
- For interviews and appearances, use type "event"
- Set the date field for each event based on the document's date headers
- If multiple days are in the document, create events for ALL days
- Keep notes concise but include key details (interviewer, concept, location details, contacts)
- Do NOT include newlines in any string values — use " / " to separate lines instead

TITLE RULES:
- Make titles short and clear
- Interviews: "GMA Interview" not "GOOD MORNING AMERICA (LIVE / ON-CAMERA)"
- Include the show/outlet name
- For breaks: "Break" or "Break / Dinner / Grooming"
- For arrivals: "Arrive at [venue]"
- For wraps: "Wrap [show] / Transfer"`;

const JSON_INSTRUCTIONS = `

CRITICAL: Return ONLY a valid JSON object. No markdown, no backticks, no explanation text.
Do NOT use newlines inside string values.
Do NOT use trailing commas.

{"events": [{"type": "flight_departure|flight_arrival|hotel|transport|event", "title": "...", "date": "YYYY-MM-DD", "timeStart": "HH:MM", "timeEnd": "HH:MM or null", "location": "...", "notes": "...", "person": null, "flightNumber": null, "departureAirport": null, "arrivalAirport": null, "passengerInitials": null, "timezone": null}]}`;

function detectDocType(fileName) {
  const lower = (fileName || '').toLowerCase();
  if (lower.includes('flight') || lower.includes('itinerary') || lower.includes('booking') || lower.includes('confirmation') || lower.includes('ticket')) return 'flight';
  return 'schedule';
}

function buildPrompt(fileName) {
  const type = detectDocType(fileName);
  const today = new Date().toISOString().slice(0, 10);
  const dateContext = `\n\nIMPORTANT: Today's date is ${today}. If a document shows a date without a year (e.g. "April 2nd", "March 15"), assume it refers to the nearest upcoming occurrence relative to today. Never default to a past year.`;
  return (type === 'flight' ? FLIGHT_PROMPT : SCHEDULE_PROMPT) + dateContext + JSON_INSTRUCTIONS;
}

function cleanJsonResponse(text) {
  let raw = text.replace(/```json|```/g, '').trim();
  // Handle truncated JSON — find last complete event object and close the structure
  if (raw.includes('"events"')) {
    // Find all complete event objects (ending with })
    const lastCompleteObj = raw.lastIndexOf('}');
    if (lastCompleteObj > 0) {
      raw = raw.substring(0, lastCompleteObj + 1);
      // Count open/close braces and brackets to close properly
      const opens = (raw.match(/\[/g) || []).length;
      const closes = (raw.match(/\]/g) || []).length;
      const openBraces = (raw.match(/\{/g) || []).length;
      const closeBraces = (raw.match(/\}/g) || []).length;
      for (let i = 0; i < openBraces - closeBraces; i++) raw += '}';
      for (let i = 0; i < opens - closes; i++) raw += ']';
      // Ensure it ends with ]}
      if (!raw.endsWith(']}')) {
        // Strip back to last complete object in the array
        const lastGoodEnd = raw.lastIndexOf('},');
        const lastGoodEnd2 = raw.lastIndexOf('}]');
        const cutPoint = Math.max(lastGoodEnd, lastGoodEnd2);
        if (cutPoint > 0) {
          raw = raw.substring(0, cutPoint + 1) + ']}';
        }
      }
    }
  } else {
    // Extract JSON object if surrounded by other text
    const jsonMatch = raw.match(/\{[\s\S]*\}/);
    if (jsonMatch) raw = jsonMatch[0];
  }
  // Fix common JSON issues from LLM output
  raw = raw
    .replace(/,\s*}/g, '}')       // trailing commas before }
    .replace(/,\s*]/g, ']')       // trailing commas before ]
    .replace(/[\x00-\x1F\x7F]/g, (c) => c === '\n' || c === '\t' || c === '\r' ? ' ' : '') // replace control chars with space
    .replace(/\t/g, ' ');         // tabs to spaces

  try {
    return JSON.parse(raw);
  } catch(e) {
    // Try fixing unescaped newlines inside strings
    raw = raw.replace(/"([^"]*)"/g, (match, content) => {
      return '"' + content.replace(/\n/g, ' ').replace(/\r/g, '') + '"';
    });
    try {
      return JSON.parse(raw);
    } catch(e2) {
      // Last resort: try to fix unescaped quotes
      raw = raw.replace(/"([^"]*?)"/g, (match, content) => {
        const fixed = content.replace(/(?<!\\)"/g, '\\"');
        return '"' + fixed + '"';
      });
      return JSON.parse(raw);
    }
  }
}

export async function extractEventsFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const base64 = e.target.result.split(',')[1];
        const mimeType = file.type.startsWith('image/') ? file.type : file.name.endsWith('.eml') ? 'text/plain' : file.type || 'application/pdf';
        const prompt = buildPrompt(file.name);

        const token = localStorage.getItem('ds_token');
        const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const res = await fetch(`${API}/sheets/extract-events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ base64, mimeType, prompt })
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Claude API error');

        const parsed = cleanJsonResponse(data.text);
        const events = parsed.events || (Array.isArray(parsed) ? parsed : []);
        resolve(events);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function extractEventsFromMultipleFiles(files) {
  const allEvents = [];
  for (const file of files) {
    const events = await extractEventsFromFile(file);
    allEvents.push(...events);
  }
  return allEvents;
}
